import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faBriefcase,
  faClock,
  faDollarSign,
  faMapMarkerAlt,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import { filterByLatestJobs, filterJobsByCriteria } from "../util";
import { useGlobalState } from "../../../../../../providers/globalState/GlobalStateProvider";
import { useNavigate } from "react-router-dom";

const FilterChip = ({ selected, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3.5 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
      selected
        ? "bg-[#15415E] text-white border-[#15415E] shadow-sm"
        : "bg-[#F3F4F6] text-[#15415E] border-transparent hover:bg-[#E8EEF2] hover:border-[#D1DCE5]"
    }`}
  >
    {label}
  </button>
);

const FilterSection = ({ icon, title, children }) => (
  <div className="pb-5 mb-5 border-b border-[#EEF2F5]">
    <div className="flex items-center gap-2 mb-3">
      <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#EAF2F6] text-[#15415E]">
        <FontAwesomeIcon icon={icon} className="text-xs" />
      </span>
      <h3 className="text-[#15415E] text-sm font-semibold tracking-wide uppercase">
        {title}
      </h3>
    </div>
    {children}
  </div>
);

const ToggleRow = ({ label, name, checked, onChange }) => (
  <label className="flex items-center justify-between py-3 cursor-pointer group">
    <span className="text-[#4B5563] text-sm font-medium group-hover:text-[#15415E] transition-colors">
      {label}
    </span>
    <span className="relative inline-flex items-center">
      <input
        type="checkbox"
        name={name}
        className="sr-only peer"
        onChange={onChange}
        checked={!!checked}
      />
      <span className="w-10 h-6 rounded-full bg-[#E5E7EB] peer-checked:bg-[#15415E] transition-colors" />
      <span className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
    </span>
  </label>
);

const formatCurrencyDisplay = (value) => {
  if (value === "" || value === null || value === undefined || value === 0) {
    return "";
  }
  const numericValue = Number(String(value).replace(/[^\d]/g, ""));
  if (Number.isNaN(numericValue) || numericValue === 0) return "";
  return numericValue.toLocaleString("en-US");
};

const parseCurrencyInput = (value) => {
  const digitsOnly = String(value ?? "").replace(/[^\d]/g, "");
  if (!digitsOnly) return "";
  return parseInt(digitsOnly, 10);
};

const CurrencyAmountInput = ({
  label,
  name,
  value,
  onChange,
  placeholder = "0",
}) => (
  <div className="flex-1">
    <label className="block text-xs text-[#6B7280] mb-1.5 font-medium">
      {label}
    </label>
    <div className="flex items-center rounded-xl border border-[#D1DCE5] bg-[#F8FBFC] focus-within:border-[#15415E] focus-within:ring-1 focus-within:ring-[#15415E]/20 overflow-hidden">
      <span className="pl-3 text-sm font-semibold text-[#15415E] select-none">
        $
      </span>
      <input
        placeholder={placeholder}
        name={name}
        type="text"
        inputMode="numeric"
        value={formatCurrencyDisplay(value)}
        onChange={onChange}
        className="w-full bg-transparent border-0 px-2 py-2.5 text-sm text-[#15415E] font-medium outline-none focus:ring-0"
      />
    </div>
  </div>
);

const FilterModal = ({
  jobsData,
  filters,
  setFilters,
  bookmarks,
  closeModal,
  setSortedBy,
  setFilteredJobs,
  setAppliedFilter,
}) => {
  const { globalState, setGlobalState } = useGlobalState();
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleRadioChange = (name, value) => {
    setFilters({
      ...filters,
      [name]: value,
    });
    if (name === "salaryRangeType") setError(null);
  };

  const handleCheckboxChange = (event) => {
    setFilters({
      ...filters,
      [event.target.name]: event.target.checked,
    });
  };

  const handleSalaryInputChange = (event, type) => {
    const { name, value } = event.target;
    const numericValue = parseCurrencyInput(value);
    const updatedRange = {
      ...filters.salaryRange,
      [name]: numericValue,
    };

    setFilters({
      ...filters,
      salaryRange: type === "hourly" ? { ...updatedRange } : updatedRange,
    });
  };

  const resetFilters = () => {
    setAppliedFilter(false);
    setFilters({
      workSetting: "",
      workType: "",
      postedBy: "",
      salaryRange: { min: 0, max: 0 },
      salaryType: "",
      multipleOpenings: false,
      bookmarkedJobs: false,
      isOCONUSFriendly: false,
      instantTeamsJobs: false,
    });
    closeModal();
    const _sortedBylatest = filterByLatestJobs(jobsData);
    setFilteredJobs(_sortedBylatest);
    setGlobalState({ ...globalState, filteredJobs: _sortedBylatest });
    navigate("/jobs");
  };

  const handleApplyFilter = () => {
    setSortedBy("datePosted");
    setAppliedFilter(true);
    if (
      filters.salaryRangeType === "salary" ||
      filters.salaryRangeType === "hourly"
    ) {
      if (!filters.salaryRange?.min || !filters.salaryRange?.max) {
        setError("Please enter both minimum and maximum values");
      } else if (filters.salaryRange?.min < filters.salaryRange?.max) {
        const _filteredJobs = filterJobsByCriteria(
          jobsData,
          filters,
          bookmarks,
        );
        const _sortedBylatest = filterByLatestJobs(_filteredJobs);
        setFilteredJobs(_sortedBylatest);
        setGlobalState({ ...globalState, filteredJobs: _sortedBylatest });
        closeModal();
      } else {
        setError("Minimum value should be less than the maximum ");
      }
    } else {
      const _filteredJobs = filterJobsByCriteria(jobsData, filters, bookmarks);
      const _sortedBylatest = filterByLatestJobs(_filteredJobs);
      setFilteredJobs(_sortedBylatest);
      setGlobalState({ ...globalState, filteredJobs: _sortedBylatest });
      closeModal();
    }
  };

  const showSalaryInputs =
    filters.salaryRangeType === "hourly" ||
    filters.salaryRangeType === "salary";

  return (
    <div
      className="w-[340px] xs:w-[92vw] xs:max-w-[340px] md:ml-[20%] lg:ml-[20%] xl:ml-[20%] 2xl:ml-[20%] bg-white shadow-2xl rounded-2xl overflow-hidden flex flex-col"
      style={{
        height: "580px",
        marginTop: "-70px",
      }}
    >
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-[#EEF2F5] bg-gradient-to-b from-white to-[#F8FBFC]">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#15415E] text-white">
              <FontAwesomeIcon icon={faSlidersH} className="text-sm" />
            </span>
            <h2 className="text-xl text-[#15415E] font-bold">Filter By</h2>
          </div>
          <button
            type="button"
            onClick={closeModal}
            className="flex items-center justify-center w-8 h-8 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] transition-colors"
            aria-label="Close filters"
          >
            <FontAwesomeIcon icon={faTimes} className="text-gray-500 text-sm" />
          </button>
        </div>
        <div className="mt-3 h-0.5 w-12 rounded-full bg-[#A9D9B7]" />
      </div>

      {/* Scrollable body */}
      <div
        className="flex-1 overflow-y-auto px-5 py-4"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#c5d0d8 #f3f4f6",
        }}
      >
        <FilterSection icon={faMapMarkerAlt} title="Work Setting">
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label="Hybrid"
              selected={filters.workSetting === "hybrid"}
              onClick={() =>
                handleRadioChange(
                  "workSetting",
                  filters.workSetting === "hybrid" ? "" : "hybrid",
                )
              }
            />
            <FilterChip
              label="Remote"
              selected={filters.workSetting === "remote"}
              onClick={() =>
                handleRadioChange(
                  "workSetting",
                  filters.workSetting === "remote" ? "" : "remote",
                )
              }
            />
           
          </div>
        </FilterSection>

        <FilterSection icon={faBriefcase} title="Work Type">
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label="Full-Time"
              selected={filters.workType === "full_time"}
              onClick={() =>
                handleRadioChange(
                  "workType",
                  filters.workType === "full_time" ? "" : "full_time",
                )
              }
            />
            <FilterChip
              label="Part-Time"
              selected={filters.workType === "part_time"}
              onClick={() =>
                handleRadioChange(
                  "workType",
                  filters.workType === "part_time" ? "" : "part_time",
                )
              }
            />
          </div>
        </FilterSection>

        <FilterSection icon={faClock} title="Posted By">
          <div className="flex flex-wrap gap-2">
            {[
              { label: "All", value: "all" },
              { label: "Last 24 hrs", value: "last24hrs" },
              { label: "Last Week", value: "lastWeek" },
              { label: "Last Month", value: "lastMonth" },
            ].map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                selected={filters.postedBy === option.value}
                onClick={() => handleRadioChange("postedBy", option.value)}
              />
            ))}
          </div>
        </FilterSection>

        <FilterSection icon={faDollarSign} title="Salary Range">
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { label: "Hourly", value: "hourly" },
              { label: "Yearly", value: "salary" },
              { label: "Free Internship", value: "unpaid_internship" },
              { label: "Commission", value: "commission" },
            ].map((option) => (
              <FilterChip
                key={option.value}
                label={option.label}
                selected={filters.salaryRangeType === option.value}
                onClick={() =>
                  handleRadioChange(
                    "salaryRangeType",
                    filters.salaryRangeType === option.value
                      ? ""
                      : option.value,
                  )
                }
              />
            ))}
          </div>

          {showSalaryInputs && (
            <div className="mt-2">
              <div className="flex gap-3">
                <CurrencyAmountInput
                  label="Min"
                  name="min"
                  value={filters.salaryRange?.min}
                  onChange={(e) => {
                    handleSalaryInputChange(
                      e,
                      filters.salaryRangeType === "hourly"
                        ? "hourly"
                        : "salary",
                    );
                    setError(null);
                  }}
                />
                <CurrencyAmountInput
                  label="Max"
                  name="max"
                  value={filters.salaryRange?.max}
                  onChange={(e) => {
                    handleSalaryInputChange(
                      e,
                      filters.salaryRangeType === "hourly"
                        ? "hourly"
                        : "salary",
                    );
                    setError(null);
                  }}
                />
              </div>
              {error && (
                <p className="text-red-500 text-xs mt-2 font-medium">{error}</p>
              )}
            </div>
          )}
        </FilterSection>

        <div className="pb-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#EAF2F6] text-[#15415E]">
              <FontAwesomeIcon icon={faSlidersH} className="text-xs" />
            </span>
            <h3 className="text-[#15415E] text-sm font-semibold tracking-wide uppercase">
              More Filters
            </h3>
          </div>
          <div className="divide-y divide-[#EEF2F5]">
            <ToggleRow
              label="Multiple Openings"
              name="multipleOpenings"
              checked={filters.multipleOpenings}
              onChange={handleCheckboxChange}
            />
            <ToggleRow
              label="OCONUS Friendly"
              name="isOCONUSFriendly"
              checked={filters.isOCONUSFriendly}
              onChange={handleCheckboxChange}
            />
            <ToggleRow
              label="Bookmarked Jobs"
              name="bookmarkedJobs"
              checked={filters.bookmarkedJobs}
              onChange={handleCheckboxChange}
            />
            <ToggleRow
              label="Instant Teams Jobs"
              name="instantTeamsJobs"
              checked={filters.instantTeamsJobs}
              onChange={handleCheckboxChange}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-[#EEF2F5] bg-white flex items-center gap-3">
        <button
          type="button"
          className="flex-1 py-2.5 text-sm font-semibold text-[#15415E] border border-[#D1DCE5] rounded-full hover:bg-[#F3F4F6] transition-colors"
          onClick={resetFilters}
        >
          Clear All
        </button>
        <button
          type="button"
          className="flex-[1.4] py-2.5 text-sm font-semibold text-white bg-[#15415E] rounded-full hover:bg-[#12354d] shadow-sm transition-colors"
          onClick={handleApplyFilter}
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default FilterModal;
